import helpers from '@/store/ui/builder/helpers'
import { FIRST_PARTY, THIRD_PARTY, SOCIO_DEMO, THIRD_PARTY_TYPES, CRITERIA_PATH, MEDIA_CONVERSION_TYPE } from '@/utils/constants'
import dataCommonGetters from '@/store/data/common/getters'
import uiCommonGetters from '@/store/ui/common/getters'
import {
    clone,
    concat,
    every,
    filter,
    find,
    findIndex,
    get,
    includes,
    isEmpty,
    isEqual,
    isNil,
    map,
    pull,
    toLower,
    toNumber,
    toString,
    reverse,
    size,
    uniq,
    values
} from 'lodash'

const getters = {
    ...dataCommonGetters,
    ...uiCommonGetters,

    getSelectableData: (_state, _getters, _rootState, rootGetters) => params => {
        if (params.type === 'custom' || params.type === 'customTaxonomies')
            return rootGetters['data/customTaxonomies/getSearchedItemsHierarchy']({
                isSelectable: true
            })
        if (params.type === 'sharing') {
            return concat(rootGetters['data/taxonomie/getSharedTaxonomies'](), rootGetters['data/taxonomies/getAllSegments']())
        }
        if (params.type === 'taxonomies') return rootGetters['data/taxonomie/getSharedTaxonomies']()
        if (params.type === 'sharedAudiences') return rootGetters['data/audience/get']
        return rootGetters['data/' + params.type + '/getSearchedItems']({
            isSelectable: true
        })
    },

    getSearchResults: (state, getters, rootState, rootGetters) => params => {
        const getSettingValue = settingName => {
            return rootGetters['data/settings/getSocioDemoValue']({ settingName })
        }
        const dataTypes = clone(rootGetters['account/getDataTypes'])
        const searchPattern = params.searchPattern
        const filterType = params.type
        const _dataTypes = params.category === 'all' ? dataTypes : params.category
        const results = {}
        let resultId = []
        let resultLabel = []
        let resultTags = []

        if (params.category === 'all') {
            //replace 'secondParty' with 'marketPlace' to get correct alphabetical order
            const _indexOfElementToReplace = findIndex(dataTypes, dt => dt === 'secondParty')
            _dataTypes.splice(_indexOfElementToReplace, 1, 'marketPlace')
        }

        if (params.setFirstCategoryToDisplay && state.menuCriteriaType !== 'customTaxonomies') {
            const toPutFirst = ['taxonomies', 'sharedAudiences'].includes(state.menuCriteriaType) ? 'sharing' : state.menuCriteriaType
            //remove menuCriteriaType from array and put it back at first place
            pull(_dataTypes, toPutFirst)
            _dataTypes.splice(0, 0, toPutFirst)
        }

        for (const dataType of _dataTypes) {
            let dataList = []
            let shouldGetResults = true
            //- CCLONE
            if (dataType === 'cclone') dataList = rootGetters['data/cclone/getAvailableCclones']({})
            //- CRITERIA
            else if (dataType === 'audience') dataList = rootGetters['data/audience/getActiveAudiences']({})
            else if (dataType === 'georadar') dataList = rootGetters['data/georadar/getActiveItems']({})
            //- SHARED
            else if (dataType === 'sharing')
                dataList = [...rootGetters['data/audience/getSharedAudiences'](), ...rootGetters['data/taxonomies/getAllSegments']()]
            else if (dataType === 'marketPlace') {
                for (const provider of rootState.data.secondParty.activeProviders) {
                    dataList.push(...rootState.data.secondParty.activeList[provider].list)
                }
            } else {
                if (dataType === 'demographic') {
                    shouldGetResults = getSettingValue('demographic')
                } else if (dataType === 'behavioral' || dataType === 'geography') {
                    shouldGetResults = getSettingValue(dataType)
                } else if (dataType === 'browser' || dataType === 'device' || dataType === 'os' || dataType === 'isp') {
                    shouldGetResults = getSettingValue('technology')
                }

                if (shouldGetResults) dataList = getters.getSelectableData({ type: dataType })
            }

            if (shouldGetResults) {
                if (filterType !== 'tag') {
                    // Search Id
                    if (filterType === 'id') {
                        if (searchPattern) resultId = filter(dataList, elem => includes(toString(elem.id), searchPattern))
                        else resultId = dataList
                    }

                    // Search Label
                    else if (filterType === 'label') {
                        if (searchPattern) resultLabel = filter(dataList, elem => includes(toLower(elem.label), toLower(searchPattern)))
                        else resultLabel = dataList
                    }
                }

                // Search Tags
                else if (filterType === 'tag') {
                    if (searchPattern)
                        resultTags = filter(dataList, elem => {
                            if (!elem.tags || size(elem.tags) === 0) return false
                            const tagLabels = map(elem.tags, tag => tag.label)
                            const foundPattern = filter(tagLabels, label => includes(toLower(label), toLower(searchPattern)))
                            return !isEmpty(foundPattern)
                        })
                    else resultTags = dataList
                }

                // Results
                const result = uniq(concat([], resultId, resultLabel, resultTags))

                //get ancestors for crm, data, geogrpahy && secondParty//marketPlace to display path in searchResults
                const dataTypeArray = ['custom', 'sharing', 'media', 'crm', 'data', 'geography', 'marketPlace', 'customTaxonomies']
                if (!isEmpty(result) && includes(dataTypeArray, dataType)) {
                    for (const _result of result) {
                        const ancestors = getters.getAncestors({
                            ancestors: [],
                            category: dataType,
                            level: Infinity,
                            node: _result
                        })
                        _result.ancestors = reverse(ancestors)
                    }
                }

                if (!isEmpty(result)) {
                    results[dataType] = result.sort((a, b) => a.label.localeCompare(b.label, undefined, { ignorePunctuation: true, numeric: true }))
                }
            }
        }

        return results
    },

    getBoxFormula: state => params => {
        return state.formula[params.boxType][params.boxIndex].criteria
    },

    getCriterionAlreadyInBox: (_state, getters) => params => {
        return !isNil(
            find(
                getters.getBoxFormula({ boxType: params.focusedBox.type, boxIndex: params.focusedBox.index }),
                data => data.criterion.name === params.criterionName
            )
        )
    },

    getJsonKeyNamesInBox: (_state, getters) => params => {
        return map(
            getters.getBoxFormula({
                boxType: params.focusedBox.type,
                boxIndex: params.focusedBox.index
            }),
            data => data.criterion.name
        )
    },

    getAncestorsJsonKeyNames: (_state, getters, _rootState, rootGetters) => params => {
        const node = params.node
        const ancestors = params.ancestors
        const nextLevel = params.level === Infinity ? Infinity : params.level - 1
        const category = params.category

        if (node && node.parentJsonKeyName !== null) {
            ancestors.push(node.parentJsonKeyName)
            let parent
            if (node.jsonKey.type === 'CustomSegment') {
                parent = rootGetters['data/customTaxonomies/getItemByJsonKeyNameHierarchy']({ jsonKeyName: node.parentJsonKeyName })
            } else if (node.jsonKey.type !== 'SecondPartyCluster') {
                parent = rootGetters['data/' + category + '/getItemByJsonKeyName']({ jsonKeyName: node.parentJsonKeyName })
            } else {
                parent = rootGetters['data/secondParty/getActiveItemByJsonKeyName']({ provider: node.jsonKey.parentName, jsonKeyName: node.parentJsonKeyName })
            }

            getters.getAncestorsJsonKeyNames({
                node: parent,
                ancestors: ancestors,
                level: nextLevel,
                category: category
            })
        }
        return ancestors
    },

    getAncestors: (_state, getters, rootState, rootGetters) => params => {
        const node = params.node
        const ancestors = params.ancestors
        const nextLevel = params.level === Infinity ? Infinity : params.level - 1
        const category = params.category
        const isSecondPartyNode = node.jsonKey.type === 'SecondPartyCluster'
        const isCustomSegment = node.jsonKey.type === 'CustomSegment'
        const isAudience = node.jsonKey.type === 'Audience'

        if (!isNil(node.parentJsonKeyName)) {
            let parent
            if (isCustomSegment) {
                if (category === 'sharing') {
                    parent = rootGetters['data/taxonomies/getSegmentByJsonKeyName'](node.parentJsonKeyName)
                } else {
                    parent = rootGetters['data/customTaxonomies/getItemByJsonKeyNameHierarchy']({ jsonKeyName: node.parentJsonKeyName })
                }
            } else if (isSecondPartyNode) {
                parent = rootGetters['data/secondParty/getActiveItemByJsonKeyName']({ provider: node.jsonKey.parentName, jsonKeyName: node.parentJsonKeyName })
            } else {
                parent = rootGetters['data/' + category + '/getSearchedItemByJsonKeyName']({
                    jsonKeyName: node.parentJsonKeyName
                })
            }
            ancestors.push(parent)
            if (!isNil(parent.parentJsonKeyName)) {
                getters.getAncestors({
                    node: parent,
                    ancestors: ancestors,
                    level: nextLevel,
                    category: category
                })
            } else {
                if (isSecondPartyNode) {
                    ancestors.push({ label: node.jsonKey.parentName })
                }
            }
        }
        if (isCustomSegment) {
            if (category === 'sharing') {
                ancestors.push({ label: rootState.data.taxonomies.shared[node.taxonomyId].label })
            } else {
                ancestors.push({ label: rootGetters['data/customTaxonomies/getWamCustomTaxoLabelById']({ taxoId: node.taxonomyId }) })
            }
        } else if (category === 'sharing' && isAudience) {
            ancestors.push({ label: 'Audience' })
        }
        return ancestors
    },

    getAllCriteriaInFormula: state => () => {
        const criteriaListInFormula = []
        for (const type in state.formula) {
            for (const index in state.formula[type]) {
                if (['inclusion', 'exclusion'].includes(type)) {
                    for (const idx in state.formula[type][index].criteria) {
                        criteriaListInFormula.push(state.formula[type][index].criteria[idx].criterion)
                    }
                }
            }
        }
        return criteriaListInFormula
    },

    getCategoryCriteria: (_state, getters) => params => {
        const _formula = getters.getAllCriteriaInFormula()
        const categoryList = params.categoryList

        if (_formula.length >= 1) {
            const categoryCriteria = _formula.filter(criterion => {
                const criterionCategory = helpers.category({
                    type: criterion.type,
                    subType: criterion.subType
                })
                return categoryList.includes(criterionCategory)
            })
            return categoryCriteria
        }
        return []
    },

    getFirstPartyCriteria: (_state, getters) => () => {
        return getters.getCategoryCriteria({
            categoryList: FIRST_PARTY
        })
    },

    isAudiencesInFormulaFirstParty: (_state, getters, rootState) => () => {
        const _formula = getters.getAllCriteriaInFormula()
        const audiences = filter(_formula, c => c.type === 'Audience')
        for (const aud of audiences) {
            if (find(rootState.data.audience.list, a => a.id === aud.ids[0]).has_1st_party) {
                return true
            }
        }
        return false
    },
    // Should not be a getter (?) Helpers ?
    getFirstPartyInFormula: (_state, getters) => () => {
        return (
            !isEmpty(
                getters.getFirstPartyCriteria({
                    categoryList: FIRST_PARTY
                })
            ) || getters.isAudiencesInFormulaFirstParty()
        )
    },

    getIsFormulaFirstPartyOnly: (_state, getters) => () => {
        const criteriaListInBox = getters.getAllCriteriaInFormula()
        const firstPartyCriteriaInFormula = getters.getFirstPartyCriteria()
        return !isEmpty(firstPartyCriteriaInFormula) && isEqual(size(firstPartyCriteriaInFormula), size(criteriaListInBox))
    },

    getIsFormulaThirdPartyOnly: (_state, getters) => () => {
        const _formula = getters.getAllCriteriaInFormula()
        if (getters.isAudiencesInFormulaFirstParty()) return false
        const thirdPartyCriteriaInFormula = getters.getThirdPartyCriteria({
            formula: _formula
        })
        return !isEmpty(thirdPartyCriteriaInFormula) && isEqual(size(thirdPartyCriteriaInFormula), size(_formula))
    },

    getThirdPartyCriteria: (_state, getters) => () => {
        return getters.getCategoryCriteria({
            categoryList: THIRD_PARTY
        })
    },

    getThirdPartyTypesCriteria: (_state, getters) => () => {
        const _formula = getters.getAllCriteriaInFormula()
        const _thirdPartyElementsInFormula = []
        for (const box of _formula) {
            if (includes(THIRD_PARTY_TYPES, box.type)) {
                _thirdPartyElementsInFormula.push(box.type)
            }
        }
        return !isEmpty(_thirdPartyElementsInFormula)
    },
    // Should not be a getter (?) Helpers ?
    getThirdPartyInFormula: (_state, getters) => () => {
        return !isEmpty(
            getters.getThirdPartyCriteria({
                categoryList: THIRD_PARTY
            })
        )
    },

    getSociodemoCriteria: (_state, getters) => () => {
        return getters.getCategoryCriteria({
            categoryList: SOCIO_DEMO
        })
    },
    // Should not be a getter (?) Helpers ?
    getSociodemoInFormula: (_state, getters) => () => {
        return !isEmpty(
            getters.getSociodemoCriteria({
                categoryList: SOCIO_DEMO
            })
        )
    },

    getCrmCriteria: (_state, getters) => () => {
        return getters.getCategoryCriteria({
            categoryList: ['crm']
        })
    },

    getV1Formula: state => () => {
        return helpers.v1Formula(state.formula)
    },

    getV2Formula: state => () => {
        return helpers.v2Formula(state.formula)
    },

    getBoxCriteriaDisabledStatus: state => params => {
        const criteria = get(state, `formula[${params.boxType}][${params.boxIndex}].criteria`, null)
        if (!isNil(criteria)) {
            return every(values(criteria), crit => crit.criterion.isDisabled)
        }
    },

    getBoxHasOnlyDisabledCriteria: state => params => {
        return state.formula[params.boxType][params.boxIndex].isDisabled
    },

    getCriteriaPath: (state, _getters, rootState) => params => {
        const _headPath = get(CRITERIA_PATH, `${params.menuCriteriaType}`, null)

        if (_headPath !== null) {
            return [_headPath, params.menuCriteriaType]
        } else if (params.menuCriteriaType === 'geography') {
            return [params.menuCriteriaType, get(rootState, 'account.country.label', '')]
        } else if (params.menuCriteriaType === 'secondParty') {
            return ['marketPlace', `${params.secondPartyCriteria}`]
        } else if (params.menuCriteriaType === 'sharedAudiences') {
            return ['sharing', 'Audiences']
        } else if (params.secondPartyCriteria === 'sharing') {
            return ['sharing', 'Taxonomies', state.currentCriterionName]
        } else {
            return ['myData', 'Taxonomies', state.currentCriterionName]
        }
    },

    /**
     *
     * @param {Object} criterion
     * @returns {Boolean}
     */
    criterionShouldShowSurfIntensity: (_state, getters, rootState) => criterion => {
        const surfIntensity = rootState.data.settings.general.surfintensity
        return getters.getSociodemoInFormula() && surfIntensity && includes(getters.getSociodemoCriteria(), criterion)
    },

    /**
     *
     * @param {Object} criterion
     * @returns {Boolean}
     */
    criterionShoudShowQuantile: (_state, getters, rootState) => criterion => {
        const quantile = rootState.data.settings.general.quantile
        return getters.getSociodemoInFormula() && quantile && includes(getters.getSociodemoCriteria(), criterion)
    },

    /**
     *
     * @param {Object} _state
     * @param {Object} _getter
     * @param {Object} _rootState
     * @param {Object} rootGetters
     * @returns {Boolean}
     *
     */
    criteriaListIsReady: (_state, _getter, _rootState, rootGetters) => {
        const CRITERIA_LIST_RELATED_MODULES = [
            'accountsDsps',
            'audience',
            'behavioral',
            'browser',
            'customTaxonomies',
            'demographic',
            'device',
            'dsp',
            'geography',
            'isp',
            'media',
            'os',
            'secondParty',
            'taxonomies'
        ]

        return rootGetters['account/isReady'] && CRITERIA_LIST_RELATED_MODULES.every(module => rootGetters[`data/${module}/isReadyForCriteriaList`]())
    },

    /**
     * This is used to format nodes like they need to be for the builder
     * @param {Object} _state
     * @param {Object} _getter
     * @param {Object} rootState
     * @param {Object} rootGetters
     * @returns {Object}
     *
     */
    getFormulaCriterionToBuilderFormat: (_state, _getter, rootState, rootGetters) => node => {
        let source
        let idIndex = 0
        const isMedia = [...MEDIA_CONVERSION_TYPE, 'WcmClick', 'WcmConversion', 'WcmImpression'].includes(node.type)

        try {
            if (node.type === 'Sociodemo') {
                source = rootState.data.demographic.list
            } else if (node.type === 'Techno') {
                idIndex = 1
                switch (node.ids[0]) {
                    case 'browser':
                        source = rootState.data.browser.list
                        break
                    case 'device':
                        source = rootState.data.device.list
                        break
                    case 'isp':
                        source = rootState.data.isp.list
                        break
                    case 'os':
                        source = rootState.data.os.list
                        break
                }
            } else if (node.type === 'Audience') {
                source = rootState.data.audience.list
            } else if (node.type === 'GeoradarID') {
                source = rootState.data.georadar.savedPoints
            } else if (node.type.startsWith('Division')) {
                source = rootState.data.geography.list
                idIndex = 1
            } else if (node.type === 'Cluster' || node.type === 'Segment') {
                source = rootState.data.behavioral.list
            } else if (isMedia) {
                source = rootState.data.media.list
            } else if (node.type === 'CustomSegment' || node.type === 'SmartSegment') {
                source = rootGetters['data/customTaxonomies/getAllHierarchies']().concat(rootGetters['data/taxonomies/getAllSegments']())
            } else if (node.type === 'SecondPartyCluster') {
                source = rootState.data.secondParty.activeList['wam-backend'].list
            }

            const storeItem = source.find(criterion => {
                if (isMedia) return isEqual(criterion.jsonKey.ids, node.ids) && criterion.jsonKey.type === node.type
                else if (node.type === 'SmartSegment') return toNumber(criterion.id) === toNumber(node.name.split('custom_segment_')[1])
                return toNumber(criterion.id) === toNumber(node.ids[idIndex])
            })

            node.label = storeItem.label
            node.parentJsonKeyName = storeItem.parentJsonKeyName
            node.jsonKey = { ...storeItem.jsonKey }
            node.isSelectable = true
            node.smartSegmentIds = storeItem.smartSegmentIds

            if (node.level && Array.isArray(node.level)) {
                node.level = node.level[0]
            }

            if (node.type !== 'SmartSegment' && node.range && Array.isArray(node.range)) {
                node.range = node.range[0]
            }

            if (node.type === 'SmartSegment') {
                node.id = storeItem.id
                node.jsonKey.ids = node.ids
                node.jsonKey.type = node.type
            }

            // Remove features if deactivated
            if (node.range && node.type === 'SmartSegment' && !rootState.data.settings.general.frequency) {
                node.ids[0] = node.id
                node.type = 'CustomSegment'
                node.jsonKey.type = 'CustomSegment'
                delete node.id
                delete node.range
                delete node.smartSegmentIds
                node.hasDisabledFeature = true
            }

            const isThirdParty = node.type === 'Cluster' || node.type === 'Segment' || node.type === 'Sociodemo'

            if (node.level && isThirdParty && !rootState.data.settings.general.surfintensity) {
                delete node.level
                node.hasDisabledFeature = true
            }
            if (node.range && isThirdParty && !rootState.data.settings.general.quantile) {
                delete node.range
                node.hasDisabledFeature = true
            }

            return node
        } catch {
            return 'deleted'
        }
    },

    getIsFormulaEmpty: state => () => {
        return state.formula.exclusion.length === 0 && state.formula.inclusion[0].criteria.length === 0
    }
}

export default getters
